import { KeyboardEvent } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../supabase/AuthProvider";
import { useUserProfile } from "../../context/UserProfileContext";
import { Feature, useEntitlements } from "../../context/EntitlementsContext";
import Overview from "./Overview";
import WorkbooksLeadershipPage from "../Workbooks/WorkbooksLeadershipPage";

export function LegacyWorkbooksRedirect() {
  const { organisationId = "" } = useParams<{ organisationId: string }>();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  search.set("tab", "workbooks");
  return <Navigate replace to={{ pathname: `/analytics/dashboard/${organisationId}`, search: `?${search}`, hash: location.hash }} />;
}

/** Route guards establish organisation access; each tab retains its own feature/role scope. */
export default function AnalyticsPage() {
  const { organizationId = "" } = useParams<{ organizationId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile, profileResolved, loading: profileLoading, error: profileError } = useUserProfile();
  const { entitlements, loading, error, hasFeature } = useEntitlements();
  const heading = <h1 className="font-display text-3xl">Analytics</h1>;
  if (!profileResolved || profileLoading || loading) return <div className="p-6">{heading}<p role="status">Verifying analytics access…</p></div>;
  if (!user || !userProfile || user.id !== userProfile.id || profileError || error || !entitlements || userProfile.status !== "active") {
    return <div className="p-6">{heading}<p role="alert">{profileError || error || "Your current account does not have access to analytics."}</p></div>;
  }
  const memberSchool = userProfile.role !== "super_admin" && String(userProfile.organisation_id) !== organizationId;
  const canOverview = memberSchool || hasFeature(Feature.ADVANCED_ANALYTICS);
  const canWorkbooks = !memberSchool && ["admin", "organisation_admin", "super_admin"].includes(userProfile.role) &&
    hasFeature(Feature.WORKBOOKS) && hasFeature(Feature.ADMIN_CONSOLE);
  const tabs = [
    ...(canOverview ? [{ id: "overview", label: "Overview" }] : []),
    ...(canWorkbooks ? [{ id: "workbooks", label: "Learning Portfolios" }] : []),
  ];
  const search = new URLSearchParams(location.search);
  const requested = search.get("tab");
  const selected = requested || tabs[0]?.id;
  const destination = (id: string) => {
    const next = new URLSearchParams(location.search);
    next.set("tab", id);
    return { pathname: location.pathname, search: `?${next}`, hash: location.hash };
  };
  if (tabs.length && ((requested && !["overview", "workbooks"].includes(requested)) || (!requested && selected === "workbooks"))) {
    return <Navigate replace to={destination(tabs[0].id)} />;
  }
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") next = (index + tabs.length - 1) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else return;
    event.preventDefault();
    navigate(destination(tabs[next].id));
    document.getElementById(`analytics-tab-${tabs[next].id}`)?.focus();
  };
  const allowed = tabs.some(tab => tab.id === selected);
  return <div className="min-w-0 bg-pd-cream text-pd-ink">
    <div className="px-4 pt-6 sm:px-6">{heading}<div role="tablist" aria-label="Analytics views" className="mt-4 flex flex-wrap gap-2 border-b border-pd-rule pb-3">
      {tabs.map((tab, index) => <button key={tab.id} type="button" role="tab" id={`analytics-tab-${tab.id}`} aria-selected={selected === tab.id} aria-controls={`analytics-panel-${tab.id}`} tabIndex={selected === tab.id || (!allowed && index === 0) ? 0 : -1} onKeyDown={event => onKeyDown(event, index)} onClick={() => navigate(destination(tab.id))} className={`rounded border px-4 py-2 text-sm font-medium ${selected === tab.id ? "border-pd-ink bg-pd-white" : "border-pd-rule"}`}>{tab.label}</button>)}
    </div></div>
    {allowed ? <div role="tabpanel" id={`analytics-panel-${selected}`} aria-labelledby={`analytics-tab-${selected}`}>
      {selected === "overview" ? <Overview /> : <WorkbooksLeadershipPage organisationId={organizationId} />}
    </div> : <p role="alert" className="p-6">Your current account does not have access to this analytics view.</p>}
  </div>;
}
